import { client } from "queryClient";
import { CalcTask, TaskStatus } from "types";

/** Seitengröße für "Mehr laden". */
export const CALC_TASK_PAGE_SIZE = 50;

/** "Alle laden" holt den Rest in großen Schritten statt in Fünfzigern. */
const LOAD_ALL_PAGE_SIZE = 1000;

export interface CalcTaskPage {
  items: CalcTask[];
  /** null, wenn keine weitere Seite existiert. */
  nextToken: string | null;
}

const pageOptions = (token: string | null, loadAll: boolean) => ({
  limit: loadAll ? LOAD_ALL_PAGE_SIZE : CALC_TASK_PAGE_SIZE,
  sortDirection: "DESC" as const,
  ...(token ? { nextToken: token } : {}),
});

/**
 * Eine Seite Laden-Preisberechnungen, neueste zuerst, über den Index calcTasks
 * (taskType + startTime). Vorher lud die Seite die ganze Tabelle per
 * ungeordnetem list() auf einmal.
 *
 * Abgefragt wird taskType "Manually": CalcTask-Zeilen entstehen nur über die
 * Buttons der Editionsseite und die Fortsetzungskette, die den Typ ihres
 * Vorgängers übernimmt.
 *
 * Mit loadAll wird ab token alles Verbleibende geholt.
 */
export const getCalcTaskList = async (
  token: string | null = null,
  loadAll = false,
): Promise<CalcTaskPage> => {
  const items: CalcTask[] = [];
  let next = token;

  do {
    const { data, nextToken } = await client.models.CalcTask.calcTasks(
      { taskType: "Manually" },
      pageOptions(next, loadAll),
    );

    items.push(...data);
    next = nextToken ?? null;
  } while (loadAll && next);

  return { items, nextToken: next };
};

/** Wie getCalcTaskList, eingeschränkt auf eine Edition. */
export const getCalcTaskListByExpansion = async (
  expansionID: string,
  token: string | null = null,
  loadAll = false,
): Promise<CalcTaskPage> => {
  const items: CalcTask[] = [];
  let next = token;

  do {
    const { data, nextToken } =
      await client.models.CalcTask.calcTasksByExpansion(
        { expansionID },
        pageOptions(next, loadAll),
      );

    items.push(...data);
    next = nextToken ?? null;
  } while (loadAll && next);

  return { items, nextToken: next };
};

export const getCalcTask = async (id: string): Promise<CalcTask | null> => {
  const { data } = await client.models.CalcTask.get({
    id,
  });

  return data;
};

export const getCalcTaskListByStatus = async (
  status: TaskStatus,
): Promise<CalcTask[]> => {
  const { data } = await client.models.CalcTask.calcTasksByStatus(
    {
      status,
    },
    {
      limit: 50,
      sortDirection: "DESC",
    },
  );

  return data;
};

export const getRunningOrPendingCalcTaskList = async (): Promise<
  CalcTask[]
> => {
  const [runningCalcTaskList, pendingCalcTaskList] = await Promise.all([
    getCalcTaskListByStatus("Running"),
    getCalcTaskListByStatus("Pending"),
  ]);

  return [...runningCalcTaskList, ...pendingCalcTaskList];
};
